import React from 'react';
import { Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

import { handleSetPasswordAction } from 'redux-modules/auth/Actions';
import { Button, Box, WrappedTextInput, SuccessPage } from 'components';
import { RootState } from 'redux-modules/store/rootState';
import { MailImage, Logo } from 'assets/index';
import { ResetPasswordSchema } from 'common/utils/validations';
import history from 'common/utils/history';

import styles from './styles';
import SignInImage from '../SignInImage';

type ComponentProps = {
  match: any;
};

type ResetPasswordType = {
  password: string;
  newPassword: string;
};

//Called when Required New password to set
const ResetPassword = ({ match }: ComponentProps) => {
  const classes = styles();
  const dispatch = useDispatch();

  const { isLoading, errorMessage, successMessage } = useSelector(
    ({ Auth }: RootState) => Auth.confirmPassword,
  );

  const { handleSubmit, control } = useForm({
    mode: 'onChange',
    resolver: yupResolver(ResetPasswordSchema),
  });

  const onSubmit = ({ password, newPassword }: ResetPasswordType) => {
    const username: string = get(match, 'params.id');
    dispatch(
      handleSetPasswordAction({
        username,
        password,
        newPassword,
      }),
    );
  };

  return (
    <Box>
      {successMessage && successMessage.length ? (
        <Box>
          <Box className={classes.successPageImgBox}>
            <img src={Logo} alt={Logo} className={classes.logoIcon} />
          </Box>
          <SuccessPage img={MailImage}>
            <Typography
              gutterBottom
              variant="h4"
              className={classes.successHeaderTxt}
            >
              {successMessage}
            </Typography>
            <Typography
              variant="h6"
              gutterBottom
              className={classes.successSubTxt}
            >
              Auto redirecting to the login page
            </Typography>
          </SuccessPage>
        </Box>
      ) : (
        <Box className={classes.gridContainer}>
          <Box className={classes.leftSide}>
            <Box className={classes.leftSideContent}>
              <Box className={classes.imgBox}>
                <img src={Logo} alt={Logo} className={classes.logoIcon} />
              </Box>
              <Typography
                variant="h2"
                gutterBottom
                className={classes.welcome8vdx}
              >
                Reset Password
              </Typography>
              <Typography className={classes.welcomeSub8vdx}>
                Create your new password.
              </Typography>
              <form
                onSubmit={handleSubmit(onSubmit)}
                data-testid="resetPasswordForm"
                className={classes.validateForm}
              >
                <Typography className={classes.labelText} variant="body1">
                  Enter New Password *
                </Typography>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Box className={classes.controllerBox}>
                      <WrappedTextInput
                        type="password"
                        // label="Password"
                        placeholder="Password"
                        className={classes.textValInput}
                        InputProps={{
                          classes: { root: classes.inputRoot },
                        }}
                        InputLabelProps={{
                          classes: {
                            root: classes.labelRoot,
                            focused: classes.labelFocused,
                          },
                        }}
                        value={value}
                        onChange={onChange}
                        error={error?.message ? true : false}
                      />
                      {error?.message && (
                        <span className={classes.errorPwd}>
                          {error?.message}
                        </span>
                      )}
                    </Box>
                  )}
                  rules={{ required: 'Password is required' }}
                />
                <Typography className={classes.labelText} variant="body1">
                  Confirm New Password *
                </Typography>
                <Controller
                  name="newPassword"
                  control={control}
                  defaultValue=""
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <Box className={classes.controllerBox}>
                      <WrappedTextInput
                        type="password"
                        // label="New Password"
                        placeholder="Min 8 character"
                        className={classes.textValInput}
                        InputProps={{
                          classes: { root: classes.inputRoot },
                        }}
                        InputLabelProps={{
                          classes: {
                            root: classes.labelRoot,
                            focused: classes.labelFocused,
                          },
                        }}
                        value={value}
                        onChange={onChange}
                        error={error?.message ? true : false}
                      />
                      {error?.message && (
                        <span className={classes.errorText}>
                          {error?.message}
                        </span>
                      )}
                    </Box>
                  )}
                  rules={{ required: 'New Password is required' }}
                />
                <Box>
                  {errorMessage && (
                    <Typography
                      variant="body1"
                      gutterBottom
                      className={classes.errorAlready}
                    >
                      {errorMessage}
                    </Typography>
                  )}
                  <Button
                    type="submit"
                    name="submit"
                    className={classes.signupBtn}
                    isLoading={isLoading}
                    disabled={isLoading}
                  />
                </Box>
              </form>
              <Box>
                <Typography className={classes.alreadyAcText}>
                  Already have an account ?
                  <a
                    onClick={() => history.push('/sign-in')}
                    rel="noreferrer"
                    className={classes.signInLink}
                  >
                    {' '}
                    Back to login
                  </a>
                </Typography>
                <Typography className={classes.copyText}>
                  © 2022 8vdX All rights reserved .
                </Typography>
              </Box>
            </Box>
          </Box>
          <SignInImage />
        </Box>
      )}
    </Box>
  );
};

export default ResetPassword;
